import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';

// The staff nav bar, one copy for every staff page. Each page names its own
// entry as `active`, which renders as a plain label instead of a button and is
// shown whatever its gate, since the page itself is already role-gated.
//
// The gates follow the routes: Customers is hidden from drivers, My History
// from owners and admins (they have History), and History, Expenses and
// Reports are owner and admin only.
export const STAFF_LINKS = [
  { key: 'dashboard', label: 'Dashboard', path: '/dashboard' },
  { key: 'customers', label: 'Customers', path: '/customers', show: (auth) => !auth.isDriver() },
  { key: 'routes', label: 'Routes', path: '/routes' },
  { key: 'my-routes', label: 'My History', path: '/my-routes', show: (auth) => !auth.isAdmin() },
  { key: 'history', label: 'History', path: '/history', show: (auth) => auth.isAdmin() },
  { key: 'expenses', label: 'Expenses', path: '/expenses', show: (auth) => auth.isAdmin() },
  { key: 'reports', label: 'Reports', path: '/reports', show: (auth) => auth.isAdmin() },
  { key: 'storm-mode', label: 'Storm Mode', path: '/storm-mode', linkClass: 'storm-link', activeClass: 'storm-active' },
  { key: 'settings', label: 'Settings', path: '/settings' },
];

// My History has always carried a shorter bar, in this order, with no Admin link.
export const MY_ROUTES_LINKS = ['routes', 'storm-mode', 'my-routes', 'settings'];

const StaffHeader = ({ active, links, adminLink = true, className = '' }) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { logout, isSuperAdmin } = auth;
  const { getAppName } = useConfig();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const items = links
    ? links.map((key) => STAFF_LINKS.find((link) => link.key === key))
    : STAFF_LINKS;
  const visible = items.filter((link) => link.key === active || !link.show || link.show(auth));
  const showAdmin = adminLink && isSuperAdmin && isSuperAdmin();

  const go = (path) => { navigate(path); setMobileMenuOpen(false); };
  const classes = (base, link) => (link.key === active
    ? `${base} active${link.activeClass ? ` ${link.activeClass}` : ''}`
    : `${base}${link.linkClass ? ` ${link.linkClass}` : ''}`);

  const navItems = (base, mobile) => visible.map((link) => (link.key === active
    ? <span key={link.key} className={classes(base, link)}>{link.label}</span>
    : <button key={link.key} onClick={() => (mobile ? go(link.path) : navigate(link.path))} className={classes(base, link)}>{link.label}</button>
  ));

  return (
    <header className={`app-header${className ? ` ${className}` : ''}`}>
      <div className="app-header-content">
        {active === 'dashboard'
          ? <span className="app-logo">{getAppName()}</span>
          : <span className="app-logo" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>{getAppName()}</span>}
        <nav className="header-nav desktop-nav" aria-label="Main navigation">
          {navItems('nav-link', false)}
          {active === 'help'
            ? <span className="nav-link help-link active" title="Documentation">?</span>
            : <button onClick={() => navigate('/docs')} className="nav-link help-link" title="Help">?</button>}
          {showAdmin && (
            <button onClick={() => navigate('/admin')} className="nav-link admin-link">Admin</button>
          )}
          <button onClick={logout} className="nav-link logout-link">Logout</button>
        </nav>
        <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu" aria-expanded={mobileMenuOpen}>
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
      {mobileMenuOpen && (
        <nav className="mobile-nav" aria-label="Mobile navigation">
          {navItems('mobile-nav-link', true)}
          {active === 'help'
            ? <span className="mobile-nav-link active">Help</span>
            : <button onClick={() => go('/docs')} className="mobile-nav-link">Help</button>}
          {showAdmin && (
            <button onClick={() => go('/admin')} className="mobile-nav-link admin-link">Admin</button>
          )}
          <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="mobile-nav-link logout-link">Logout</button>
        </nav>
      )}
    </header>
  );
};

// The platform admin bar: the two product sites, then back to the app, Help
// and Logout, with no hamburger. It needs no config provider.
export const PlatformAdminHeader = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  return (
    <header className="admin-header">
      <div className="admin-header-content">
        <h1 className="admin-logo">Platform Admin</h1>
        <nav className="admin-nav">
          <a href="https://snowtrack.pro" target="_blank" rel="noopener noreferrer" className="nav-link app-link snow-link">
            SnowTrack™
          </a>
          <a href="https://lawntrack.pro" target="_blank" rel="noopener noreferrer" className="nav-link app-link lawn-link">
            LawnTrack
          </a>
          <button onClick={() => navigate('/dashboard')} className="nav-link">
            Dashboard
          </button>
          <button onClick={() => navigate('/docs')} className="nav-link" title="Help">
            Help
          </button>
          <button onClick={logout} className="nav-link logout-link">
            Logout
          </button>
        </nav>
      </div>
    </header>
  );
};

export default StaffHeader;
